import React, { useState } from "react";
import api from "../../../../api/api";
import CarPurchaseAgreementPdf from "./CarPurchaseAgreementPdf";
import { pdf } from "@react-pdf/renderer";
import QRCode from "qrcode";
import Swal from "sweetalert2";

import {
  TextField,
  Button,
  Grid,
  Typography,
  Paper,
  Box,
  Divider,
} from "@mui/material";

const initialState = {
  seller_name: "",
  seller_address: "",
  seller_phone: "",
  seller_email: "",

  buyer_name: "",
  buyer_address: "",
  buyer_phone: "",
  buyer_email: "",

  make: "",
  model: "",
  year: "",
  vin: "",
  mileage: "",

  total_price: "",
  down_payment: "",
  second_payment: "",
  remaining_balance: "",

  installment_amount: "",
  installment_frequency: "Monthly",
  number_of_installments: "",
  first_installment_date: "",

  late_payment_fee: "",
  late_payment_frequency: "Per Month",

  state_law: "",
};

function CarPurchaseAgreement() {
  const [form, setForm] = useState(initialState);
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    setForm({
      ...form,
      [e.target.name]: e.target.value,
    });
  };

  // =========================
  // VALIDATION (SWAL)
  // =========================
  const validateForm = () => {
    const requiredFields = [
      "seller_name",
      "seller_email",
      "buyer_name",
      "buyer_email",
      "make",
      "model",
      "year",
      "vin",
      "total_price",
    ];

    for (let field of requiredFields) {
      if (!form[field] || form[field].trim() === "") {
        Swal.fire({
          icon: "warning",
          title: "Missing Field",
          text: `Please fill in ${field.replaceAll("_", " ")}`,
        });
        return false;
      }
    }

    return true;
  };

  // =========================
  // SUBMIT HANDLER
  // =========================
  const handleSubmit = async () => {
    try {
      if (!validateForm()) return;

      setLoading(true);

      const payload = {
        ...form,
        total_price: parseFloat(form.total_price || 0),
        down_payment: parseFloat(form.down_payment || 0),
        second_payment: parseFloat(form.second_payment || 0),
        remaining_balance: parseFloat(form.remaining_balance || 0),

        installment_amount: parseFloat(form.installment_amount || 0),
        number_of_installments: parseInt(form.number_of_installments || 0),

        late_payment_fee: parseFloat(form.late_payment_fee || 0),

        state_law: form.state_law?.trim() || undefined,
      };

      const res = await api.post("/car-agreements/", payload);
      const savedData = res.data;

      // QR CODE
      const qrData = JSON.stringify({
        seller: savedData.seller_name,
        buyer: savedData.buyer_name,
        vehicle: `${savedData.make} ${savedData.model} ${savedData.year}`,
        vin: savedData.vin,
        price: savedData.total_price,
      });

      const qrCode = await QRCode.toDataURL(qrData);

      // PDF GENERATION
      const blob = await pdf(
        <CarPurchaseAgreementPdf
          data={{ ...savedData, qrCode }}
        />
      ).toBlob();

      const url = URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = url;
      link.download = `car-agreement-${savedData.id}.pdf`;
      link.click();

      URL.revokeObjectURL(url);

      // =========================
      // SUCCESS ALERT
      // =========================
      Swal.fire({
        icon: "success",
        title: "Success",
        text: "Car purchase agreement generated successfully!",
        timer: 2000,
        showConfirmButton: false,
      });

      setForm(initialState);
    } catch (err) {
      console.error(err);

      // =========================
      // ERROR ALERT
      // =========================
      Swal.fire({
        icon: "error",
        title: "Failed",
        text:
          err.response?.data?.detail ||
          "Error generating agreement PDF.",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Paper
      elevation={4}
      sx={{
        maxWidth: 1200,
        mx: "auto",
        p: { xs: 2, md: 4 },
        borderRadius: 3,
        background: "#ffffff",
      }}
    >
      {/* HEADER */}
      <Box sx={{ mb: 4 }}>
        <Typography
          variant="h4"
          fontWeight="bold"
          sx={{
            color: "#111827",
            mb: 1,
            fontSize: {
              xs: "1.6rem",
              md: "2rem",
            },
          }}
        >
          Car Purchase Agreement
        </Typography>

        <Typography
          sx={{
            color: "#6B7280",
            fontSize: "0.95rem",
          }}
        >
          Complete the sections below to generate a professional
          car purchase agreement PDF document.
        </Typography>
      </Box>

      <Grid container spacing={3}>

        {/* ===================================================== */}
        {/* SELLER SECTION */}
        {/* ===================================================== */}
        <Grid item xs={12}>
          <Box
            sx={{
              mb: 1,
              mt: 1,
            }}
          >
            <Typography
              variant="h6"
              fontWeight="bold"
              sx={{
                color: "#111827",
                fontSize: "1rem",
              }}
            >
              Seller Information
            </Typography>

            <Divider
              sx={{
                mt: 1,
                borderColor: "#111827",
              }}
            />
          </Box>
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            name="seller_name"
            label="Seller Name"
            value={form.seller_name}
            onChange={handleChange}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            type="email"
            name="seller_email"
            label="Seller Email"
            value={form.seller_email}
            onChange={handleChange}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            name="seller_phone"
            label="Seller Phone"
            value={form.seller_phone}
            onChange={handleChange}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            name="seller_address"
            label="Seller Address"
            value={form.seller_address}
            onChange={handleChange}
          />
        </Grid>

        {/* ===================================================== */}
        {/* BUYER SECTION */}
        {/* ===================================================== */}
        <Grid item xs={12}>
          <Box sx={{ mt: 2, mb: 1 }}>
            <Typography
              variant="h6"
              fontWeight="bold"
              sx={{
                color: "#111827",
                fontSize: "1rem",
              }}
            >
              Buyer Information
            </Typography>

            <Divider
              sx={{
                mt: 1,
                borderColor: "#111827",
              }}
            />
          </Box>
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            name="buyer_name"
            label="Buyer Name"
            value={form.buyer_name}
            onChange={handleChange}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            type="email"
            name="buyer_email"
            label="Buyer Email"
            value={form.buyer_email}
            onChange={handleChange}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            name="buyer_phone"
            label="Buyer Phone"
            value={form.buyer_phone}
            onChange={handleChange}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            name="buyer_address"
            label="Buyer Address"
            value={form.buyer_address}
            onChange={handleChange}
          />
        </Grid>

        {/* ===================================================== */}
        {/* VEHICLE SECTION */}
        {/* ===================================================== */}
        <Grid item xs={12}>
          <Box sx={{ mt: 2, mb: 1 }}>
            <Typography
              variant="h6"
              fontWeight="bold"
              sx={{
                color: "#111827",
                fontSize: "1rem",
              }}
            >
              Vehicle Information
            </Typography>

            <Divider
              sx={{
                mt: 1,
                borderColor: "#111827",
              }}
            />
          </Box>
        </Grid>

        <Grid item xs={12} md={4}>
          <TextField
            fullWidth
            name="make"
            label="Vehicle Make"
            value={form.make}
            onChange={handleChange}
          />
        </Grid>

        <Grid item xs={12} md={4}>
          <TextField
            fullWidth
            name="model"
            label="Vehicle Model"
            value={form.model}
            onChange={handleChange}
          />
        </Grid>

        <Grid item xs={12} md={4}>
          <TextField
            fullWidth
            name="year"
            label="Vehicle Year"
            value={form.year}
            onChange={handleChange}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            name="vin"
            label="Vehicle VIN"
            value={form.vin}
            onChange={handleChange}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            name="mileage"
            label="Vehicle Mileage"
            value={form.mileage}
            onChange={handleChange}
          />
        </Grid>

        {/* ===================================================== */}
        {/* FINANCIAL SECTION */}
        {/* ===================================================== */}
        <Grid item xs={12}>
          <Box sx={{ mt: 2, mb: 1 }}>
            <Typography
              variant="h6"
              fontWeight="bold"
              sx={{
                color: "#111827",
                fontSize: "1rem",
              }}
            >
              Financial Information
            </Typography>

            <Divider
              sx={{
                mt: 1,
                borderColor: "#111827",
              }}
            />
          </Box>
        </Grid>

        <Grid item xs={12} md={3}>
          <TextField
            fullWidth
            name="total_price"
            label="Total Price"
            value={form.total_price}
            onChange={handleChange}
          />
        </Grid>

        <Grid item xs={12} md={3}>
          <TextField
            fullWidth
            name="down_payment"
            label="Down Payment"
            value={form.down_payment}
            onChange={handleChange}
          />
        </Grid>

        <Grid item xs={12} md={3}>
          <TextField
            fullWidth
            name="second_payment"
            label="Second Payment"
            value={form.second_payment}
            onChange={handleChange}
          />
        </Grid>

        <Grid item xs={12} md={3}>
          <TextField
            fullWidth
            name="remaining_balance"
            label="Remaining Balance"
            value={form.remaining_balance}
            onChange={handleChange}
          />
        </Grid>

        {/* INSTALLMENT PLAN */}

        <Grid item xs={12}>
  <Box sx={{ mt: 2, mb: 1 }}>
    <Typography variant="h6" fontWeight="bold" sx={{ fontSize: "1rem" }}>
      Installment Plan
    </Typography>
    <Divider sx={{ mt: 1, borderColor: "#111827" }} />
  </Box>
</Grid>

<Grid item xs={12} md={3}>
  <TextField
    fullWidth
    name="installment_amount"
    label="Installment Amount"
    value={form.installment_amount}
    onChange={handleChange}
  />
</Grid>

<Grid item xs={12} md={3}>
  <TextField
    fullWidth
    select
    SelectProps={{ native: true }}
    name="installment_frequency"
    label="Frequency"
    value={form.installment_frequency}
    onChange={handleChange}
  >
    <option value="Weekly">Weekly</option>
    <option value="Bi-Weekly">Bi-Weekly</option>
    <option value="Monthly">Monthly</option>
  </TextField>
</Grid>

<Grid item xs={12} md={3}>
  <TextField
    fullWidth
    name="number_of_installments"
    label="No. of Installments"
    value={form.number_of_installments}
    onChange={handleChange}
  />
</Grid>

<Grid item xs={12} md={3}>
  <TextField
    fullWidth
    type="date"
    name="first_installment_date"
    label="First Installment Date"
    InputLabelProps={{ shrink: true }}
    value={form.first_installment_date}
    onChange={handleChange}
  />
</Grid>

{/* LATE PAYMENT SECTION */}

<Grid item xs={12}>
  <Box sx={{ mt: 2, mb: 1 }}>
    <Typography variant="h6" fontWeight="bold" sx={{ fontSize: "1rem" }}>
      Late Payment / Default Terms
    </Typography>
    <Divider sx={{ mt: 1, borderColor: "#111827" }} />
  </Box>
</Grid>

<Grid item xs={12} md={6}>
  <TextField
    fullWidth
    name="late_payment_fee"
    label="Late Payment Fee"
    value={form.late_payment_fee}
    onChange={handleChange}
  />
</Grid>

<Grid item xs={12} md={6}>
  <TextField
    fullWidth
    select
    SelectProps={{ native: true }}
    name="late_payment_frequency"
    label="Late Fee Frequency"
    value={form.late_payment_frequency}
    onChange={handleChange}
  >
    <option value="Per Day">Per Day</option>
    <option value="Per Week">Per Week</option>
    <option value="Per Month">Per Month</option>
  </TextField>
</Grid>

        {/* ===================================================== */}
        {/* GOVERNING LAW SECTION */}
        {/* ===================================================== */}
        <Grid item xs={12}>
          <Box sx={{ mt: 2, mb: 1 }}>
            <Typography
              variant="h6"
              fontWeight="bold"
              sx={{
                color: "#111827",
                fontSize: "1rem",
              }}
            >
              Governing Law
            </Typography>

            <Divider
              sx={{
                mt: 1,
                borderColor: "#111827",
              }}
            />
          </Box>
        </Grid>

        <Grid item xs={12}>
          <TextField
            fullWidth
            name="state_law"
            label="State Law"
            placeholder="Example: Texas"
            value={form.state_law}
            onChange={handleChange}
          />
        </Grid>

        {/* ===================================================== */}
        {/* BUTTON */}
        {/* ===================================================== */}
        <Grid item xs={12}>
          <Button
            variant="contained"
            fullWidth
            onClick={handleSubmit}
            disabled={loading}
            sx={{
              mt: 2,
              py: 1.6,
              borderRadius: 2,
              fontWeight: "bold",
              fontSize: "0.95rem",
              background: "#111827",

              "&:hover": {
                background: "#000000",
              },
            }}
          >
            {loading
              ? "Generating Agreement..."
              : "Generate Agreement & Download PDF"}
          </Button>
        </Grid>

      </Grid>
    </Paper>
  );
}

export default CarPurchaseAgreement;




// import React, { useState } from "react";
// import api from "../../../../api/api";
// import CarPurchaseAgreementPdf from "./CarPurchaseAgreementPdf";
// import { pdf } from "@react-pdf/renderer";
// import QRCode from "qrcode";

// import {
//   TextField,
//   Button,
//   Grid,
//   Typography,
//   Paper,
//   Box,
//   Divider,
// } from "@mui/material";

// const initialState = {
//   // Seller
//   seller_name: "",
//   seller_address: "",
//   seller_phone: "",
//   seller_email: "",

//   // Buyer
//   buyer_name: "",
//   buyer_address: "",
//   buyer_phone: "",
//   buyer_email: "",

//   // Vehicle
//   make: "",
//   model: "",
//   year: "",
//   vin: "",
//   mileage: "",

//   // Financial
//   total_price: "",
//   down_payment: "",
//   second_payment: "",
//   remaining_balance: "",

//     // NEW: Installments (DYNAMIC)
//   installment_amount: "",
//   installment_frequency: "Monthly",
//   number_of_installments: "",
//   first_installment_date: "",

//   // NEW: Late Payment (DYNAMIC)
//   late_payment_fee: "",
//   late_payment_frequency: "Per Month",

//   // Law
//   state_law: "",
// };

// function CarPurchaseAgreement() {
//   const [form, setForm] = useState(initialState);
//   const [loading, setLoading] = useState(false);

//   const handleChange = (e) => {
//     setForm({
//       ...form,
//       [e.target.name]: e.target.value,
//     });
//   };

//   const validateForm = () => {
//     const requiredFields = [
//       "seller_name",
//       "seller_email",
//       "buyer_name",
//       "buyer_email",
//       "make",
//       "model",
//       "year",
//       "vin",
//       "total_price",
//     ];

//     for (let field of requiredFields) {
//       if (!form[field] || form[field].trim() === "") {
//         alert(`Please fill in ${field.replace("_", " ")}`);
//         return false;
//       }
//     }

//     return true;
//   };

//   const handleSubmit = async () => {
//     try {
//       if (!validateForm()) return;

//       setLoading(true);

// const payload = {
//   ...form,

//   total_price: parseFloat(form.total_price || 0),
//   down_payment: parseFloat(form.down_payment || 0),
//   second_payment: parseFloat(form.second_payment || 0),
//   remaining_balance: parseFloat(form.remaining_balance || 0),

//   installment_amount: parseFloat(form.installment_amount || 0),
//   number_of_installments: parseInt(form.number_of_installments || 0),

//   late_payment_fee: parseFloat(form.late_payment_fee || 0),

//   state_law: form.state_law?.trim() || undefined,
// };

//       const res = await api.post("/car-agreements/", payload);

//       const savedData = res.data;

//       // QR DATA
//       const qrData = JSON.stringify({
//         seller: savedData.seller_name,
//         buyer: savedData.buyer_name,
//         vehicle: `${savedData.make} ${savedData.model} ${savedData.year}`,
//         vin: savedData.vin,
//         price: savedData.total_price,
//       });

//       const qrCode = await QRCode.toDataURL(qrData);

//       // GENERATE PDF
//       const blob = await pdf(
//         <CarPurchaseAgreementPdf
//           data={{
//             ...savedData,
//             qrCode,
//           }}
//         />
//       ).toBlob();

//       const url = URL.createObjectURL(blob);

//       const link = document.createElement("a");

//       link.href = url;
//       link.download = `car-agreement-${savedData.id}.pdf`;

//       link.click();

//       URL.revokeObjectURL(url);

//       setForm(initialState);

//     } catch (err) {
//       console.error(err);

//       alert(
//         err.response?.data
//           ? "Validation error. Check console."
//           : "Error generating agreement PDF."
//       );
//     } finally {
//       setLoading(false);
//     }
//   };

//   return (
//     <Paper
//       elevation={4}
//       sx={{
//         maxWidth: 1200,
//         mx: "auto",
//         p: { xs: 2, md: 4 },
//         borderRadius: 3,
//         background: "#ffffff",
//       }}
//     >
//       {/* HEADER */}
//       <Box sx={{ mb: 4 }}>
//         <Typography
//           variant="h4"
//           fontWeight="bold"
//           sx={{
//             color: "#111827",
//             mb: 1,
//             fontSize: {
//               xs: "1.6rem",
//               md: "2rem",
//             },
//           }}
//         >
//           Car Purchase Agreement
//         </Typography>

//         <Typography
//           sx={{
//             color: "#6B7280",
//             fontSize: "0.95rem",
//           }}
//         >
//           Complete the sections below to generate a professional
//           car purchase agreement PDF document.
//         </Typography>
//       </Box>

//       <Grid container spacing={3}>

//         {/* ===================================================== */}
//         {/* SELLER SECTION */}
//         {/* ===================================================== */}
//         <Grid item xs={12}>
//           <Box
//             sx={{
//               mb: 1,
//               mt: 1,
//             }}
//           >
//             <Typography
//               variant="h6"
//               fontWeight="bold"
//               sx={{
//                 color: "#111827",
//                 fontSize: "1rem",
//               }}
//             >
//               Seller Information
//             </Typography>

//             <Divider
//               sx={{
//                 mt: 1,
//                 borderColor: "#111827",
//               }}
//             />
//           </Box>
//         </Grid>

//         <Grid item xs={12} md={6}>
//           <TextField
//             fullWidth
//             name="seller_name"
//             label="Seller Name"
//             value={form.seller_name}
//             onChange={handleChange}
//           />
//         </Grid>

//         <Grid item xs={12} md={6}>
//           <TextField
//             fullWidth
//             type="email"
//             name="seller_email"
//             label="Seller Email"
//             value={form.seller_email}
//             onChange={handleChange}
//           />
//         </Grid>

//         <Grid item xs={12} md={6}>
//           <TextField
//             fullWidth
//             name="seller_phone"
//             label="Seller Phone"
//             value={form.seller_phone}
//             onChange={handleChange}
//           />
//         </Grid>

//         <Grid item xs={12} md={6}>
//           <TextField
//             fullWidth
//             name="seller_address"
//             label="Seller Address"
//             value={form.seller_address}
//             onChange={handleChange}
//           />
//         </Grid>

//         {/* ===================================================== */}
//         {/* BUYER SECTION */}
//         {/* ===================================================== */}
//         <Grid item xs={12}>
//           <Box sx={{ mt: 2, mb: 1 }}>
//             <Typography
//               variant="h6"
//               fontWeight="bold"
//               sx={{
//                 color: "#111827",
//                 fontSize: "1rem",
//               }}
//             >
//               Buyer Information
//             </Typography>

//             <Divider
//               sx={{
//                 mt: 1,
//                 borderColor: "#111827",
//               }}
//             />
//           </Box>
//         </Grid>

//         <Grid item xs={12} md={6}>
//           <TextField
//             fullWidth
//             name="buyer_name"
//             label="Buyer Name"
//             value={form.buyer_name}
//             onChange={handleChange}
//           />
//         </Grid>

//         <Grid item xs={12} md={6}>
//           <TextField
//             fullWidth
//             type="email"
//             name="buyer_email"
//             label="Buyer Email"
//             value={form.buyer_email}
//             onChange={handleChange}
//           />
//         </Grid>

//         <Grid item xs={12} md={6}>
//           <TextField
//             fullWidth
//             name="buyer_phone"
//             label="Buyer Phone"
//             value={form.buyer_phone}
//             onChange={handleChange}
//           />
//         </Grid>

//         <Grid item xs={12} md={6}>
//           <TextField
//             fullWidth
//             name="buyer_address"
//             label="Buyer Address"
//             value={form.buyer_address}
//             onChange={handleChange}
//           />
//         </Grid>

//         {/* ===================================================== */}
//         {/* VEHICLE SECTION */}
//         {/* ===================================================== */}
//         <Grid item xs={12}>
//           <Box sx={{ mt: 2, mb: 1 }}>
//             <Typography
//               variant="h6"
//               fontWeight="bold"
//               sx={{
//                 color: "#111827",
//                 fontSize: "1rem",
//               }}
//             >
//               Vehicle Information
//             </Typography>

//             <Divider
//               sx={{
//                 mt: 1,
//                 borderColor: "#111827",
//               }}
//             />
//           </Box>
//         </Grid>

//         <Grid item xs={12} md={4}>
//           <TextField
//             fullWidth
//             name="make"
//             label="Vehicle Make"
//             value={form.make}
//             onChange={handleChange}
//           />
//         </Grid>

//         <Grid item xs={12} md={4}>
//           <TextField
//             fullWidth
//             name="model"
//             label="Vehicle Model"
//             value={form.model}
//             onChange={handleChange}
//           />
//         </Grid>

//         <Grid item xs={12} md={4}>
//           <TextField
//             fullWidth
//             name="year"
//             label="Vehicle Year"
//             value={form.year}
//             onChange={handleChange}
//           />
//         </Grid>

//         <Grid item xs={12} md={6}>
//           <TextField
//             fullWidth
//             name="vin"
//             label="Vehicle VIN"
//             value={form.vin}
//             onChange={handleChange}
//           />
//         </Grid>

//         <Grid item xs={12} md={6}>
//           <TextField
//             fullWidth
//             name="mileage"
//             label="Vehicle Mileage"
//             value={form.mileage}
//             onChange={handleChange}
//           />
//         </Grid>

//         {/* ===================================================== */}
//         {/* FINANCIAL SECTION */}
//         {/* ===================================================== */}
//         <Grid item xs={12}>
//           <Box sx={{ mt: 2, mb: 1 }}>
//             <Typography
//               variant="h6"
//               fontWeight="bold"
//               sx={{
//                 color: "#111827",
//                 fontSize: "1rem",
//               }}
//             >
//               Financial Information
//             </Typography>

//             <Divider
//               sx={{
//                 mt: 1,
//                 borderColor: "#111827",
//               }}
//             />
//           </Box>
//         </Grid>

//         <Grid item xs={12} md={3}>
//           <TextField
//             fullWidth
//             name="total_price"
//             label="Total Price"
//             value={form.total_price}
//             onChange={handleChange}
//           />
//         </Grid>

//         <Grid item xs={12} md={3}>
//           <TextField
//             fullWidth
//             name="down_payment"
//             label="Down Payment"
//             value={form.down_payment}
//             onChange={handleChange}
//           />
//         </Grid>

//         <Grid item xs={12} md={3}>
//           <TextField
//             fullWidth
//             name="second_payment"
//             label="Second Payment"
//             value={form.second_payment}
//             onChange={handleChange}
//           />
//         </Grid>

//         <Grid item xs={12} md={3}>
//           <TextField
//             fullWidth
//             name="remaining_balance"
//             label="Remaining Balance"
//             value={form.remaining_balance}
//             onChange={handleChange}
//           />
//         </Grid>

//         {/* INSTALLMENT PLAN */}

//         <Grid item xs={12}>
//   <Box sx={{ mt: 2, mb: 1 }}>
//     <Typography variant="h6" fontWeight="bold" sx={{ fontSize: "1rem" }}>
//       Installment Plan
//     </Typography>
//     <Divider sx={{ mt: 1, borderColor: "#111827" }} />
//   </Box>
// </Grid>

// <Grid item xs={12} md={3}>
//   <TextField
//     fullWidth
//     name="installment_amount"
//     label="Installment Amount"
//     value={form.installment_amount}
//     onChange={handleChange}
//   />
// </Grid>

// <Grid item xs={12} md={3}>
//   <TextField
//     fullWidth
//     select
//     SelectProps={{ native: true }}
//     name="installment_frequency"
//     label="Frequency"
//     value={form.installment_frequency}
//     onChange={handleChange}
//   >
//     <option value="Weekly">Weekly</option>
//     <option value="Bi-Weekly">Bi-Weekly</option>
//     <option value="Monthly">Monthly</option>
//   </TextField>
// </Grid>

// <Grid item xs={12} md={3}>
//   <TextField
//     fullWidth
//     name="number_of_installments"
//     label="No. of Installments"
//     value={form.number_of_installments}
//     onChange={handleChange}
//   />
// </Grid>

// <Grid item xs={12} md={3}>
//   <TextField
//     fullWidth
//     type="date"
//     name="first_installment_date"
//     label="First Installment Date"
//     InputLabelProps={{ shrink: true }}
//     value={form.first_installment_date}
//     onChange={handleChange}
//   />
// </Grid>

// {/* LATE PAYMENT SECTION */}

// <Grid item xs={12}>
//   <Box sx={{ mt: 2, mb: 1 }}>
//     <Typography variant="h6" fontWeight="bold" sx={{ fontSize: "1rem" }}>
//       Late Payment / Default Terms
//     </Typography>
//     <Divider sx={{ mt: 1, borderColor: "#111827" }} />
//   </Box>
// </Grid>

// <Grid item xs={12} md={6}>
//   <TextField
//     fullWidth
//     name="late_payment_fee"
//     label="Late Payment Fee"
//     value={form.late_payment_fee}
//     onChange={handleChange}
//   />
// </Grid>

// <Grid item xs={12} md={6}>
//   <TextField
//     fullWidth
//     select
//     SelectProps={{ native: true }}
//     name="late_payment_frequency"
//     label="Late Fee Frequency"
//     value={form.late_payment_frequency}
//     onChange={handleChange}
//   >
//     <option value="Per Day">Per Day</option>
//     <option value="Per Week">Per Week</option>
//     <option value="Per Month">Per Month</option>
//   </TextField>
// </Grid>

//         {/* ===================================================== */}
//         {/* GOVERNING LAW SECTION */}
//         {/* ===================================================== */}
//         <Grid item xs={12}>
//           <Box sx={{ mt: 2, mb: 1 }}>
//             <Typography
//               variant="h6"
//               fontWeight="bold"
//               sx={{
//                 color: "#111827",
//                 fontSize: "1rem",
//               }}
//             >
//               Governing Law
//             </Typography>

//             <Divider
//               sx={{
//                 mt: 1,
//                 borderColor: "#111827",
//               }}
//             />
//           </Box>
//         </Grid>

//         <Grid item xs={12}>
//           <TextField
//             fullWidth
//             name="state_law"
//             label="State Law"
//             placeholder="Example: Texas"
//             value={form.state_law}
//             onChange={handleChange}
//           />
//         </Grid>

//         {/* ===================================================== */}
//         {/* BUTTON */}
//         {/* ===================================================== */}
//         <Grid item xs={12}>
//           <Button
//             variant="contained"
//             fullWidth
//             onClick={handleSubmit}
//             disabled={loading}
//             sx={{
//               mt: 2,
//               py: 1.6,
//               borderRadius: 2,
//               fontWeight: "bold",
//               fontSize: "0.95rem",
//               background: "#111827",

//               "&:hover": {
//                 background: "#000000",
//               },
//             }}
//           >
//             {loading
//               ? "Generating Agreement..."
//               : "Generate Agreement & Download PDF"}
//           </Button>
//         </Grid>

//       </Grid>
//     </Paper>
//   );
// }

// export default CarPurchaseAgreement;

